'use client';

/**
 * The Sources, Mediation and ads.txt sections of the Ads admin tab (ads
 * build plan section 5.2, "Sources becomes connectable rows").
 *
 * One row per source: on/off (the gate's `sources` list, so it is saved with
 * the gate), status, what it still needs, the environment variables it reads
 * (by NAME, with set / well-formed; a value is never sent to the browser),
 * test mode, tier, timeout, floor, weight, the country and category
 * allowlists, the consent requirement, the source's own ids, what it
 * receives, the browser-direct warning where the viewer's browser talks to
 * the network itself, and a Test button that runs the adapter against its
 * recorded fixture and shows the request and the normalised candidate.
 *
 * The component edits a structured draft (`AdsSourcesSettings`) held by the
 * tab; list and JSON fields keep their typed text locally so a half-typed
 * comma or brace does not fight the cursor, and apply once they parse. The
 * server validates everything again on save (`adsSourcesSchema`).
 *
 * Admin labels stay in English like the other admin tabs; nothing here is
 * seen by a person who sees an ad.
 *
 * THE TEST RESULT SITS UNDER ITS TEST BUTTON (the 2026-09-30 fix round: it
 * used to land at the bottom of a long row, far from the button that asked
 * for it). It is drawn right under the row's header, where the button is,
 * announced politely, and scrolled into view after every run.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, FlaskConical, KeyRound, ShieldCheck } from 'lucide-react';

import type { AdsSourcesSettings, AdSourceRow } from '@/lib/ads/source-settings';
import type { AdNetworkSourceId, AdSource, AdSourceId } from '@/lib/ads/types';
import { cn } from '@/lib/utils';

export interface SourceReadoutRow {
  id: AdSourceId;
  label: string;
  status: 'connected' | 'not_connected';
  runMode: 'live' | 'fixture' | null;
  trackerMode: 'server' | 'browser-direct';
  deferred: boolean;
  testFlag: string | null;
  receives: string;
  browserWarning: string | null;
  note: string | null;
  env: Array<{ name: string; set: boolean; valid: boolean; what: string }>;
  gaps: string[];
  breakerOpen: boolean;
  /** The refresh triggers the source fills (the refresh round, 2026-09-30); absent from an older pod's readout. */
  refresh?: Array<'time' | 'navigation'>;
}

interface TestResult {
  source: string;
  request: { method: string; url: string; headers: Record<string, string>; body?: unknown } | null;
  noBid: boolean;
  error: string | null;
  candidates: Array<{
    adId: string;
    tier: string;
    text: { advertiser: string; headline: string; description?: string; displayHost?: string; cta?: string };
    clickUrl: string;
    trackerMode: string;
    trackers: { impression: string[]; viewable: string[]; click: string[]; billing?: string };
    price: { model: string; amount?: number; ecpm: number; basis: string };
    dsa: { behalf: string; paid: string };
    adomain: string[];
  }>;
  rejects: Array<{ reason: string; detail: string | null }>;
}

// One 32 px control height for inputs, selects and buttons (the design pass of
// 2026-09-30 measured 33, 34 and 32 px side by side). A textarea keeps its rows.
const inputClass = 'h-8 w-full rounded-md border border-border bg-background px-2 text-sm';
const textareaClass = 'w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm';
const buttonClass = 'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-3 text-sm hover:bg-muted disabled:opacity-50';

/**
 * The consent requirement of a source (lib/ads/types.ts
 * `AD_CONSENT_REQUIREMENTS`), as a short option name that fits the closed
 * select and the full sentence shown under the row's grid. GPC is the
 * browser's Global Privacy Control signal; TCF is the IAB Transparency and
 * Consent Framework, whose consent string a consent banner produces.
 */
type ConsentRequirement = AdSourceRow['consent'];
const CONSENT_LABELS: Record<ConsentRequirement, string> = {
  none: 'None (contextual)',
  gpc: 'Off with GPC',
  tcf: 'GPC and TCF consent',
};
const CONSENT_HELP: Record<ConsentRequirement, string> = {
  none: 'none. The source is asked for every request, whatever privacy signal the browser sends.',
  gpc: 'off when the browser sends Global Privacy Control (GPC).',
  tcf: 'off when the browser sends Global Privacy Control (GPC), and off in the EEA, the UK and Switzerland unless the request carries a TCF (Transparency and Consent Framework) consent string.',
};
/** The OpenRTB source is held to `tcf` in code whatever its row says. */
function consentOf(id: string, stored: ConsentRequirement): ConsentRequirement {
  return id === 'openrtb' ? 'tcf' : stored;
}

function listText(values: readonly (string | number)[]): string {
  return values.join(', ');
}

function parseList(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseIds(text: string): number[] {
  return parseList(text)
    .map((s) => Number(s))
    .filter((n) => Number.isInteger(n) && n > 0);
}

function ct31Text(map: Record<string, string[]>): string {
  return Object.entries(map)
    .map(([cat, ids]) => `${cat}: ${ids.join(', ')}`)
    .join('\n');
}

function parseCt31(text: string): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const line of text.split('\n')) {
    const [cat, ids] = line.split(':');
    if (!cat?.trim() || !ids) continue;
    const list = parseList(ids);
    if (list.length > 0) out[cat.trim().toLowerCase()] = list;
  }
  return out;
}

/** A text field that edits a parsed value: the typed text is kept, the parsed value applied on every change. */
function TextField({
  label,
  hint,
  initial,
  disabled,
  multiline,
  onValue,
  placeholder,
}: {
  label: string;
  hint?: string;
  initial: string;
  disabled: boolean;
  multiline?: boolean;
  onValue: (text: string) => string | null;
  placeholder?: string;
}) {
  const [text, setText] = useState(initial);
  const [problem, setProblem] = useState<string | null>(null);
  const change = (value: string) => {
    setText(value);
    setProblem(onValue(value));
  };
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      {multiline ? (
        <textarea value={text} disabled={disabled} rows={3} placeholder={placeholder} onChange={(e) => change(e.target.value)} className={cn(textareaClass, 'font-mono text-xs')} />
      ) : (
        <input type="text" value={text} disabled={disabled} placeholder={placeholder} onChange={(e) => change(e.target.value)} className={inputClass} />
      )}
      {problem ? <span className="mt-1 block text-xs text-destructive">{problem}</span> : hint ? <span className="mt-1 block text-xs text-muted-foreground">{hint}</span> : null}
    </label>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  step,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  disabled: boolean;
  onChange: (n: number) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step ?? 1}
        disabled={disabled}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        className={inputClass}
      />
    </label>
  );
}

function StatusChip({ row }: { row: SourceReadoutRow }) {
  if (row.runMode === 'fixture') {
    return <span className="rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-xs">Test mode (fixture)</span>;
  }
  return row.status === 'connected' ? (
    <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs">Configured</span>
  ) : (
    <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">Not configured</span>
  );
}

/**
 * One source's Test outcome, under the row's header where the Test button is.
 * `run` changes with every answer, so a second Test of the same source scrolls
 * its (possibly identical) result into view again.
 */
function TestOutcome({ id, test, run }: { id: string; test: TestResult | string; run: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    let reduce = false;
    try {
      reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    } catch {
      reduce = false;
    }
    el.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
  }, [run]);
  return (
    <div ref={ref} id={`ads-test-${id}`} data-testid={`ads-test-result-${id}`} role="status" aria-live="polite" className="mt-2">
      {typeof test === 'string' ? <p className="text-xs text-destructive">{test}</p> : <TestView result={test} />}
    </div>
  );
}

function TestView({ result }: { result: TestResult }) {
  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3 text-xs">
      {result.candidates.length === 0 ? (
        <p className="font-medium">{result.noBid ? 'No fill from the fixture.' : 'No candidate passed.'}</p>
      ) : null}
      {result.candidates.map((c) => (
        <div key={c.adId} className="space-y-1">
          <div className="text-sm">
            <span className="mr-1.5 rounded-sm bg-foreground/85 px-1 py-px text-[9px] font-bold uppercase text-background">Ad</span>
            <span className="font-semibold">{c.text.advertiser}</span> · {c.text.headline}
          </div>
          <div className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
            <div>Paid for by: {c.dsa.paid}</div>
            <div>On behalf of: {c.dsa.behalf}</div>
            <div>Tier: {c.tier}</div>
            <div>
              Price: {c.price.model}
              {c.price.amount !== undefined ? ` ${c.price.amount}` : ''}, eCPM {c.price.ecpm.toFixed(3)} ({c.price.basis})
            </div>
            <div>Advertiser domains: {c.adomain.join(', ') || 'none'}</div>
            <div>Trackers: {c.trackerMode}</div>
            <div className="sm:col-span-2 break-all">Click: {c.clickUrl}</div>
            {c.text.description ? <div className="sm:col-span-2">Description: {c.text.description}</div> : null}
          </div>
        </div>
      ))}
      {result.rejects.length > 0 ? (
        <div>
          <span className="font-medium">Refused: </span>
          {result.rejects.map((r) => `${r.reason}${r.detail ? ` (${r.detail})` : ''}`).join(', ')}
        </div>
      ) : null}
      {result.request ? (
        <details>
          <summary className="cursor-pointer">The request it would send (not sent)</summary>
          <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 font-mono text-[11px]">
            {`${result.request.method} ${result.request.url}\n${Object.entries(result.request.headers)
              .map(([k, v]) => `${k}: ${v}`)
              .join('\n')}${result.request.body !== undefined ? `\n\n${JSON.stringify(result.request.body, null, 2)}` : ''}`}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

export function AdsSourcesPanel({
  rows,
  enabled,
  onToggle,
  settings,
  onChange,
  canWrite,
}: {
  rows: SourceReadoutRow[];
  enabled: readonly AdSource[];
  onToggle: (id: AdSourceId, on: boolean) => void;
  settings: AdsSourcesSettings;
  onChange: (next: AdsSourcesSettings) => void;
  canWrite: boolean;
}) {
  const [tests, setTests] = useState<Record<string, TestResult | string>>({});
  // How many answers each source's Test has had: the result scrolls into view on each.
  const [testRuns, setTestRuns] = useState<Record<string, number>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const disabled = !canWrite;

  const isOn = useCallback(
    (id: AdSourceId) => enabled.includes(id) || (id === 'kevel' && enabled.includes('robutler')) || (id === 'openrtb' && enabled.includes('exchange')),
    [enabled],
  );

  const setRow = useCallback(
    <K extends AdNetworkSourceId>(id: K, patch: Partial<AdsSourcesSettings[K]>) => {
      onChange({ ...settings, [id]: { ...settings[id], ...patch } });
    },
    [settings, onChange],
  );

  const runTest = useCallback(async (id: AdNetworkSourceId) => {
    setTesting(id);
    try {
      const res = await fetch('/api/admin/ads/sources/test', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ source: id }),
      });
      const body = (await res.json().catch(() => null)) as TestResult | { error?: string } | null;
      if (!res.ok || !body || !('candidates' in body)) {
        setTests((t) => ({ ...t, [id]: (body as { error?: string } | null)?.error ?? `Test failed (${res.status})` }));
      } else {
        setTests((t) => ({ ...t, [id]: body }));
      }
    } catch {
      setTests((t) => ({ ...t, [id]: 'Test failed' }));
    } finally {
      setTestRuns((r) => ({ ...r, [id]: (r[id] ?? 0) + 1 }));
      setTesting(null);
    }
  }, []);

  const adsTxtPreview = useMemo(() => ['OWNERDOMAIN=robutler.ai', ...settings.adsTxt].join('\n'), [settings.adsTxt]);

  return (
    <>
      <section className="rounded-lg border border-border p-4">
        <h2 className="text-sm font-semibold">Sources</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Every source but house ads is off until it is switched on here, and runs live only with its credentials set as server environment variables
          (never shown here) and its row complete. Test mode answers from the recorded fixture: nothing is sent and nothing fires, but the fixture ad
          competes like any other and is shown to whoever the gate admits, so test with Only these people. Switching a source on is saved with the gate.
        </p>
        <div className="mt-3 space-y-3">
          {rows.map((row) => {
            const on = isOn(row.id);
            if (row.id === 'house') {
              return (
                <div key={row.id} className="rounded-md border border-border p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <input type="checkbox" checked={on} disabled={disabled} onChange={(e) => onToggle('house', e.target.checked)} aria-label="House ads on" />
                    <span className="font-medium">{row.label}</span>
                    <StatusChip row={row} />
                    <span className="text-xs text-muted-foreground">{row.receives}</span>
                  </div>
                  <div className="mt-2 max-w-[10rem]">
                    <NumberField
                      label="Weight (ties)"
                      value={settings.house.weight}
                      min={1}
                      max={100}
                      disabled={disabled}
                      onChange={(n) => onChange({ ...settings, house: { weight: n } })}
                    />
                  </div>
                </div>
              );
            }
            const id = row.id as AdNetworkSourceId;
            const s: AdSourceRow = settings[id];
            const test = tests[id];
            return (
              <div key={id} className={cn('rounded-md border border-border p-3', !on && 'opacity-80')}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <input type="checkbox" checked={on} disabled={disabled} onChange={(e) => onToggle(id, e.target.checked)} aria-label={`${row.label} on`} />
                  <span className="font-medium">{row.label}</span>
                  <StatusChip row={row} />
                  <span className="rounded-full border border-border px-2 py-0.5 text-xs">
                    {row.trackerMode === 'server' ? 'Trackers from our server' : 'Trackers from the browser'}
                  </span>
                  {row.deferred ? <span className="rounded-full border border-border px-2 py-0.5 text-xs">Called only once chosen</span> : null}
                  {row.refresh ? (
                    <span
                      className="rounded-full border border-border px-2 py-0.5 text-xs"
                      title={
                        row.refresh.includes('time')
                          ? 'Fills a timed refresh and a refresh on navigation (a window, screen or tab change after the ad was in view).'
                          : "Its terms allow no timed rotation: it fills only a refresh on navigation (a window, screen or tab change after the ad was in view 45 s)."
                      }
                    >
                      {row.refresh.includes('time') ? 'Refresh: timer and navigation' : 'Refresh: navigation only'}
                    </span>
                  ) : null}
                  {row.breakerOpen ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs">
                      <AlertTriangle className="h-3 w-3" />
                      Paused: too many errors
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => void runTest(id)}
                    disabled={testing !== null}
                    aria-controls={test ? `ads-test-${id}` : undefined}
                    className={cn(buttonClass, 'ml-auto')}
                  >
                    <FlaskConical className="h-3.5 w-3.5" />
                    {testing === id ? 'Testing…' : 'Test'}
                  </button>
                </div>
                {test ? <TestOutcome id={id} test={test} run={testRuns[id] ?? 0} /> : null}

                <div className="mt-2 flex flex-wrap items-start gap-x-4 gap-y-1 text-xs">
                  {row.env.map((v) => (
                    <span key={v.name} className="inline-flex items-center gap-1" title={v.what}>
                      <KeyRound className="h-3 w-3" />
                      <code>{v.name}</code>
                      <span className={v.valid ? 'text-emerald-600' : 'text-muted-foreground'}>{v.valid ? 'set' : v.set ? 'malformed' : 'not set'}</span>
                    </span>
                  ))}
                  {row.gaps.length > 0 ? <span className="text-muted-foreground">Still needs: {row.gaps.join('; ')}.</span> : null}
                </div>

                <p className="mt-2 text-xs text-muted-foreground">
                  <ShieldCheck className="mr-1 inline h-3 w-3" />
                  {row.receives}
                </p>
                {row.browserWarning ? (
                  <p className="mt-2 flex gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {row.browserWarning}
                  </p>
                ) : null}
                {row.note ? <p className="mt-2 text-xs text-muted-foreground">{row.note}</p> : null}

                <div className="mt-3 grid items-end gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  <label className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={s.testMode} disabled={disabled} onChange={(e) => setRow(id, { testMode: e.target.checked })} />
                    Test mode
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs text-muted-foreground">Tier</span>
                    <select value={s.tier} disabled={disabled} onChange={(e) => setRow(id, { tier: e.target.value as AdSourceRow['tier'] })} className={inputClass}>
                      <option value="direct">Direct</option>
                      <option value="network">Network</option>
                    </select>
                  </label>
                  <NumberField label="Timeout (ms)" value={s.timeoutMs} min={50} max={1000} disabled={disabled} onChange={(n) => setRow(id, { timeoutMs: n })} />
                  <NumberField label="Floor (USD eCPM)" value={s.floorEcpm} min={0} max={100} step={0.01} disabled={disabled} onChange={(n) => setRow(id, { floorEcpm: n })} />
                  <NumberField label="Weight (ties)" value={s.weight} min={1} max={100} disabled={disabled} onChange={(n) => setRow(id, { weight: n })} />
                  <TextField
                    label="Countries (two-letter codes; empty is everywhere)"
                    initial={listText(s.geo)}
                    disabled={disabled}
                    onValue={(t) => {
                      const list = parseList(t).map((c) => c.toUpperCase());
                      const bad = list.filter((c) => !/^[A-Z]{2}$/.test(c));
                      setRow(id, { geo: list.filter((c) => /^[A-Z]{2}$/.test(c)) });
                      return bad.length ? `Not codes: ${bad.join(', ')}` : null;
                    }}
                  />
                  <TextField
                    label="Categories (empty is every category)"
                    initial={listText(s.categories)}
                    disabled={disabled}
                    onValue={(t) => {
                      setRow(id, { categories: parseList(t).map((c) => c.toLowerCase()) });
                      return null;
                    }}
                  />
                  <label className="block text-sm">
                    <span className="mb-1 block text-xs text-muted-foreground">Consent needed</span>
                    {/* Short option names: the select is one cell of a
                        four-column grid (about 215 px), and the full sentence
                        was cut off mid-word in the closed control ("Off with
                        GPC, and in the E", the design pass of 2026-09-30).
                        The sentence is the line under the grid. */}
                    <select
                      value={consentOf(id, s.consent)}
                      disabled={disabled || id === 'openrtb'}
                      onChange={(e) => setRow(id, { consent: e.target.value as AdSourceRow['consent'] })}
                      title={CONSENT_HELP[consentOf(id, s.consent)]}
                      aria-describedby={`ads-consent-help-${id}`}
                      className={inputClass}
                    >
                      <option value="none">{CONSENT_LABELS.none}</option>
                      <option value="gpc">{CONSENT_LABELS.gpc}</option>
                      <option value="tcf">{CONSENT_LABELS.tcf}</option>
                    </select>
                  </label>
                </div>
                <p id={`ads-consent-help-${id}`} className="mt-2 text-xs text-muted-foreground" data-testid={`ads-consent-help-${id}`}>
                  Consent: {CONSENT_HELP[consentOf(id, s.consent)]}
                </p>

                {id === 'openrtb' ? (
                  <div className="mt-3 grid items-end gap-3 sm:grid-cols-2">
                    <TextField
                      label="Auction endpoint (https)"
                      placeholder="https://prebid.example.com/openrtb2/auction"
                      initial={settings.openrtb.endpoint}
                      disabled={disabled}
                      onValue={(t) => {
                        setRow('openrtb', { endpoint: t.trim() });
                        return t.trim() && !t.trim().startsWith('https://') ? 'The endpoint must start with https://.' : null;
                      }}
                    />
                    <label className="block text-sm">
                      <span className="mb-1 block text-xs text-muted-foreground">Profile</span>
                      <select
                        value={settings.openrtb.profile}
                        disabled={disabled}
                        onChange={(e) => setRow('openrtb', { profile: e.target.value as 'prebid' | 'generic' })}
                        className={inputClass}
                      >
                        <option value="prebid">Prebid Server</option>
                        <option value="generic">Any OpenRTB 2.6 exchange</option>
                      </select>
                    </label>
                    <TextField
                      label="Site id at the exchange"
                      initial={settings.openrtb.siteId}
                      disabled={disabled}
                      onValue={(t) => {
                        setRow('openrtb', { siteId: t.trim() });
                        return null;
                      }}
                    />
                    <TextField
                      label="Exchange domain (supply chain)"
                      placeholder="exchange.example"
                      initial={settings.openrtb.asi}
                      disabled={disabled}
                      onValue={(t) => {
                        setRow('openrtb', { asi: t.trim().toLowerCase() });
                        return null;
                      }}
                    />
                    <TextField
                      label="Bidder params (JSON, Prebid profile)"
                      placeholder='{ "appnexus": { "placementId": 123 } }'
                      multiline
                      initial={Object.keys(settings.openrtb.bidders).length ? JSON.stringify(settings.openrtb.bidders, null, 2) : ''}
                      disabled={disabled}
                      onValue={(t) => {
                        if (!t.trim()) {
                          setRow('openrtb', { bidders: {} });
                          return null;
                        }
                        try {
                          const parsed = JSON.parse(t) as unknown;
                          if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return 'An object of bidder names.';
                          setRow('openrtb', { bidders: parsed as Record<string, Record<string, unknown>> });
                          return null;
                        } catch {
                          return 'Not valid JSON yet.';
                        }
                      }}
                    />
                    <TextField
                      label="Content Taxonomy 3.1 ids per category (one per line)"
                      placeholder="productivity: 123, 456"
                      multiline
                      initial={ct31Text(settings.openrtb.ct31)}
                      disabled={disabled}
                      onValue={(t) => {
                        setRow('openrtb', { ct31: parseCt31(t) });
                        return null;
                      }}
                    />
                  </div>
                ) : null}
                {id === 'ethicalads' ? (
                  <div className="mt-3 grid items-end gap-3 sm:grid-cols-2">
                    <TextField
                      label="Publisher slug"
                      initial={settings.ethicalads.publisher}
                      disabled={disabled}
                      onValue={(t) => {
                        setRow('ethicalads', { publisher: t.trim() });
                        return null;
                      }}
                    />
                    <TextField
                      label="Custom text ad type (from EthicalAds)"
                      initial={settings.ethicalads.adType}
                      disabled={disabled}
                      onValue={(t) => {
                        setRow('ethicalads', { adType: t.trim() });
                        return null;
                      }}
                    />
                  </div>
                ) : null}
                {id === 'kevel' ? (
                  <div className="mt-3 grid items-end gap-3 sm:grid-cols-2">
                    <TextField
                      label="Ad type ids"
                      initial={listText(settings.kevel.adTypes)}
                      disabled={disabled}
                      onValue={(t) => {
                        setRow('kevel', { adTypes: parseIds(t) });
                        return null;
                      }}
                    />
                    <TextField
                      label="Zone ids (optional)"
                      initial={listText(settings.kevel.zoneIds)}
                      disabled={disabled}
                      onValue={(t) => {
                        setRow('kevel', { zoneIds: parseIds(t) });
                        return null;
                      }}
                    />
                  </div>
                ) : null}
                {id === 'buysellads' ? (
                  <label className="mt-3 flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={settings.buysellads.policyWaived}
                      disabled={disabled}
                      onChange={(e) => setRow('buysellads', { policyWaived: e.target.checked })}
                    />
                    BuySellAds has waived Carbon&apos;s placement policy for us in writing
                  </label>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-lg border border-border p-4">
        <h2 className="text-sm font-semibold">Mediation</h2>
        <div className="mt-2 flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="ads-mediation-mode"
              checked={settings.mediation.mode === 'priority'}
              disabled={disabled}
              onChange={() => onChange({ ...settings, mediation: { ...settings.mediation, mode: 'priority' } })}
            />
            Priority: direct, then networks, then house; eCPM within a tier
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="ads-mediation-mode"
              checked={settings.mediation.mode === 'firstPrice'}
              disabled={disabled}
              onChange={() => onChange({ ...settings, mediation: { ...settings.mediation, mode: 'firstPrice' } })}
            />
            First price: the highest eCPM above its floor
          </label>
        </div>
        <div className="mt-3 grid items-end gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <NumberField
            label="Budget for all sources (ms)"
            value={settings.mediation.budgetMs}
            min={100}
            max={1000}
            disabled={disabled}
            onChange={(n) => onChange({ ...settings, mediation: { ...settings.mediation, budgetMs: n } })}
          />
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={settings.mediation.holdout.enabled}
              disabled={disabled}
              onChange={(e) => onChange({ ...settings, mediation: { ...settings.mediation, holdout: { ...settings.mediation.holdout, enabled: e.target.checked } } })}
            />
            Holdout: one source alone for a share of traffic
          </label>
          <NumberField
            label="Holdout share (%)"
            value={Math.round(settings.mediation.holdout.share * 100)}
            min={1}
            max={50}
            disabled={disabled || !settings.mediation.holdout.enabled}
            onChange={(n) => onChange({ ...settings, mediation: { ...settings.mediation, holdout: { ...settings.mediation.holdout, share: n / 100 } } })}
          />
          <label className="block text-sm">
            <span className="mb-1 block text-xs text-muted-foreground">DSA transparency</span>
            <select
              value={settings.mediation.dsaRequired}
              disabled={disabled}
              onChange={(e) => onChange({ ...settings, mediation: { ...settings.mediation, dsaRequired: Number(e.target.value) === 3 ? 3 : 2 } })}
              className={inputClass}
            >
              <option value={2}>Required</option>
              <option value={3}>Required, as an online platform</option>
            </select>
          </label>
          <TextField
            label="Blocked advertiser domains"
            initial={listText(settings.mediation.badv)}
            disabled={disabled}
            onValue={(t) => {
              onChange({ ...settings, mediation: { ...settings.mediation, badv: parseList(t).map((d) => d.toLowerCase()) } });
              return null;
            }}
          />
          <TextField
            label="Blocked Ad Product 2.0 categories"
            initial={listText(settings.mediation.bcat)}
            disabled={disabled}
            onValue={(t) => {
              onChange({ ...settings, mediation: { ...settings.mediation, bcat: parseList(t) } });
              return null;
            }}
          />
        </div>
      </section>

      <section className="rounded-lg border border-border p-4">
        <h2 className="text-sm font-semibold">ads.txt</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Served at the apex only. OWNERDOMAIN is always first. Add a DIRECT line only for a seller account we hold, in the form domain, account id,
          DIRECT (and a certification id when the seller has one).
        </p>
        <div className="mt-2 grid items-end gap-3 sm:grid-cols-2">
          <TextField
            label="DIRECT lines (one per line)"
            placeholder="exchange.example, 12345, DIRECT"
            multiline
            initial={settings.adsTxt.join('\n')}
            disabled={disabled}
            onValue={(t) => {
              const lines = t
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean);
              onChange({ ...settings, adsTxt: lines });
              const bad = lines.filter((l) => !/,\s*DIRECT(\s*,\s*[A-Za-z0-9]+)?$/i.test(l));
              return bad.length ? `Not DIRECT lines: ${bad.join(' | ')}` : null;
            }}
          />
          <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-2 font-mono text-xs">{adsTxtPreview}</pre>
        </div>
      </section>
    </>
  );
}
